import React from 'react';
import { ChevronDownIcon } from '../Icons';
import styles from './MultiSelect.module.css';

export interface Option {
  label: string;
  value: string;
}

interface MultiSelectProps {
  label: string;
  options: Option[];
  selected: string[];
  onChange: (values: string[]) => void;
  placeholder?: string;
}

const CheckMark: React.FC = () => (
  <svg width={12} height={12} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round">
    <path d="m5 12 5 5L20 6" />
  </svg>
);

const MultiSelect: React.FC<MultiSelectProps> = ({
  label,
  options,
  selected,
  onChange,
  placeholder = 'All',
}) => {
  const [open, setOpen] = React.useState(false);
  const [query, setQuery] = React.useState('');
  const ref = React.useRef<HTMLDivElement>(null);
  const menuRef = React.useRef<HTMLDivElement>(null);

  React.useEffect(() => {
    if (!open) return;
    const onClickOutside = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    /**
     * 🔴 **En fase de captura** (19-oct-2026). El `Modal` detiene el `mousedown` de su panel para no cerrarse al hacer
     * clic adentro, así que en la fase normal el clic nunca llegaba al documento: abrir un menú no cerraba el anterior y
     * en el modal de filtros quedaban tres abiertos, uno encima de otro. En captura, el documento lo oye antes que
     * nadie lo detenga.
     */
    document.addEventListener('mousedown', onClickOutside, true);
    return () => document.removeEventListener('mousedown', onClickOutside, true);
  }, [open]);

  /**
   * Al abrir, el menú se asoma completo (19-oct-2026). Dentro de un modal con scroll, el de los últimos campos quedaba
   * cortado por el pie del modal; así el modal se desplaza lo justo para verlo. 📌 `?.` porque jsdom no lo implementa.
   */
  React.useEffect(() => {
    if (open) menuRef.current?.scrollIntoView?.({ block: 'nearest' });
  }, [open]);

  const toggle = (value: string) => {
    if (selected.includes(value)) {
      onChange(selected.filter((v) => v !== value));
    } else {
      onChange([...selected, value]);
    }
  };

  const filtered = options.filter((o) =>
    o.label.toLowerCase().includes(query.toLowerCase())
  );

  return (
    <div className={[styles.field, open ? styles.open : ''].join(' ')} ref={ref}>
      <span className={styles.label}>{label}</span>
      <button
        type="button"
        className={styles.control}
        onClick={() => setOpen((o) => !o)}
      >
        <span className={selected.length === 0 ? styles.placeholder : undefined}>
          {selected.length === 0 ? placeholder : `${selected.length} selected`}
        </span>
        <span className={styles.chevron}>
          <ChevronDownIcon size={16} />
        </span>
      </button>

      {open && (
        <div className={styles.menu} ref={menuRef}>
          <input
            className={styles.search}
            placeholder="Search..."
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoFocus
          />
          <div className={styles.list}>
            {filtered.length === 0 ? (
              <div className={styles.emptyOpt}>No options</div>
            ) : (
              filtered.map((o) => {
                const isChecked = selected.includes(o.value);
                return (
                  <button
                    key={o.value}
                    type="button"
                    className={styles.option}
                    onClick={() => toggle(o.value)}
                  >
                    <span className={[styles.check, isChecked ? styles.checked : ''].join(' ')}>
                      {isChecked && <CheckMark />}
                    </span>
                    {o.label}
                  </button>
                );
              })
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default MultiSelect;
